/**
 * The frame every screen sits in.
 *
 * The menu is not written here. It is assembled from what the modules declare,
 * filtered by what this person is allowed to do - so a cashier simply has no
 * Reports entry, rather than a greyed-out one, and a new module appears in the
 * menu without this file being touched.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useI18n, useT } from '../i18n/index.js';
import { useAuth } from '../state/auth.js';
import { useSettings } from '../state/settings.js';
import { useSync } from '../sync.js';
import { allNav, featureOn, itemAllowed, primaryNav, NAV_GROUP_LABEL, type NavGroup } from '../module.js';
import { Button } from './ui.js';
import { StorageNotice } from './StorageNotice.js';
import { PRINT_PROBLEM } from '../printing.js';
import { useToast } from '../state/toast.js';
import { ShopMark } from './ShopMark.js';
import { TillAlert } from './TillAlert.js';
import { SubscriptionNotice } from './SubscriptionNotice.js';
import { StaffAlert } from './StaffAlert.js';

/** Online / offline / sending, with the number still waiting. On every screen. */
export function StatusPill() {
  const t = useT();
  const { state, pending, waitingHours, flush } = useSync();
  const { settings } = useSettings();

  const stale = waitingHours !== null && waitingHours > settings.unsyncedWarnHours;
  const cls = stale ? 'stale' : state === 'online' ? '' : state;
  const label = state === 'syncing' ? t('net.syncing') : state === 'offline' ? t('net.offline') : t('net.online');

  return (
    <button
      className={`status ${cls}`}
      onClick={() => void flush()}
      title={state === 'offline' ? t('net.offlineNote') : t('net.syncNow')}
    >
      <span className="dot" aria-hidden />
      {label}
      {pending > 0 ? ` · ${t('net.waiting', { n: pending })}` : ''}
    </button>
  );
}

const GROUPS: NavGroup[] = ['sell', 'stock', 'money', 'setup'];

export function AppShell({ children, title }: { children: ReactNode; title?: string }) {
  const t = useT();
  const { lang, setLang } = useI18n();
  const { user, signOut, can } = useAuth();
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  /* Only one folded group is open at a time, so the menu never gets long. */
  const [openGroup, setOpenGroup] = useState<NavGroup | null>(null);

  useEffect(() => setMenuOpen(false), [pathname]);

  // A print that could not go through the helper went to the print window
  // instead; say why, so a closed helper does not look like a broken printer.
  const toast = useToast();
  useEffect(() => {
    const on = (e: Event) => {
      const d = String((e as CustomEvent).detail ?? '');
      toast.warn(t('print.fellBack', { why: d === 'not_running' || d === 'timeout' ? t('print.notRunning') : d }));
    };
    window.addEventListener(PRINT_PROBLEM, on);
    return () => window.removeEventListener(PRINT_PROBLEM, on);
  }, [toast, t]);

  // Land on a folded screen - from a link, or a refresh - and its group opens.
  useEffect(() => {
    const here = allNav().find((i) => !i.primary && i.to === pathname);
    if (here) setOpenGroup(here.group);
  }, [pathname]);

  const visible = allNav().filter((i) => itemAllowed(i, can) && featureOn(i, settings));
  /* The menu proper is the everyday list; the rest sits folded underneath it. */
  const menu = primaryNav().filter((i) => itemAllowed(i, can) && featureOn(i, settings));
  const rest = visible.filter((i) => !i.primary);
  const bottom = visible
    .filter((i) => i.bottom)
    .sort((a, b) => (a.bottom ?? 0) - (b.bottom ?? 0))
    .slice(0, 4);

  // A shop that filled in only one language still has a name in the other.
  const storeName =
    (lang === 'ar'
      ? settings.storeNameAr || settings.storeNameEn
      : settings.storeNameEn || settings.storeNameAr) || t('app.name');
  const roleName = lang === 'ar' ? user?.roleNameAr : user?.roleNameEn;

  return (
    <div className="shell">
      {menuOpen ? <div className="scrim" onClick={() => setMenuOpen(false)} /> : null}

      <nav className={`nav ${menuOpen ? 'open' : ''}`} aria-label={t('common.menu')}>
        <div className="nav-brand">
          <ShopMark name={storeName} logo={settings.logoDataUrl} />
          <div style={{ minWidth: 0 }}>
            <div className="nav-brand-name">{storeName}</div>
            <div className="nav-brand-sub">
              {user?.name}
              {roleName ? ` · ${roleName}` : ''}
            </div>
          </div>
        </div>

        {menu.map((i) => (
          <NavLink key={i.to} to={i.to} title={i.hintKey ? t(i.hintKey) : undefined}>
            <span className="ico" aria-hidden>{i.icon}</span>
            {t(i.labelKey)}
          </NavLink>
        ))}

        {/*
          * Everything a shop touches once a week or once a year: receiving,
          * the books, the settings. Folded away, because a menu you have to
          * read twenty entries of is a menu nobody reads.
          */}
        {GROUPS.map((g) => {
          const items = rest.filter((i) => i.group === g);
          if (items.length === 0) return null;
          const open = openGroup === g;
          return (
            <div key={g} className="nav-sec">
              <button
                type="button"
                className={`nav-sec-head ${open ? 'on' : ''}`}
                aria-expanded={open}
                onClick={() => setOpenGroup(open ? null : g)}
              >
                <span className="chev" aria-hidden>{open ? '\u25be' : '\u25b8'}</span>
                {t(NAV_GROUP_LABEL[g])}
              </button>
              {open
                ? items.map((i) => (
                    <NavLink key={i.to} to={i.to} title={i.hintKey ? t(i.hintKey) : undefined}>
                      <span className="ico" aria-hidden>{i.icon}</span>
                      {t(i.labelKey)}
                    </NavLink>
                  ))
                : null}
            </div>
          );
        })}

        <div className="nav-spacer" />

        <div className="col" style={{ gap: 'var(--s-2)', padding: 'var(--s-2)' }}>
          <Button size="sm" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} block>
            {lang === 'ar' ? 'English' : 'العربية'}
          </Button>
          <Button size="sm" variant="ghost" onClick={signOut} block>
            {t('common.signOut')}
          </Button>
        </div>
      </nav>

      <div className="main">
        <header className="header">
          <Button variant="ghost" className="icon menu-btn" onClick={() => setMenuOpen(true)} aria-label={t('common.menu')}>
            ☰
          </Button>
          <h1>{title ?? ''}</h1>
          <div className="header-spacer" />
          <StatusPill />
        </header>

        <StorageNotice />
        <TillAlert />
        <SubscriptionNotice />
        <StaffAlert />
        <main className="content">{children}</main>

        {bottom.length > 1 ? (
          <nav className="bottom-nav" aria-label={t('common.menu')}>
            {bottom.map((i) => (
              <NavLink key={i.to} to={i.to}>
                <span className="ico" aria-hidden>{i.icon}</span>
                {t(i.labelKey)}
              </NavLink>
            ))}
            <button className="bn-more" onClick={() => setMenuOpen(true)}>
              <span className="ico" aria-hidden>☰</span>
              {t('common.more')}
            </button>
          </nav>
        ) : null}
      </div>
    </div>
  );
}
